import { Link } from 'react-router-dom';
import type { Blog } from '../types/blog';

interface BlogCardProps {
    blog: Blog;
    showEdit?: boolean;
}

export function BlogCard({ blog, showEdit = false }: BlogCardProps) {
    const formattedDate = new Date(blog.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    return (
        <article className="group relative overflow-hidden rounded-2xl bg-base-200 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
            <Link
                to={`/blog/${blog.slug}`}
                className="h-full flex flex-col text-inherit no-underline"
            >
                <div className="relative aspect-video w-full overflow-hidden bg-base-300">
                    {blog.thumbnail_image ? (
                        <img
                            src={blog.thumbnail_image}
                            alt={blog.title}
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                    ) : (
                        <div className="h-full w-full flex items-center justify-center text-base-content/20 font-bold text-4xl select-none">
                            {blog.title.charAt(0).toUpperCase()}
                        </div>
                    )}

                    <div className="absolute inset-0 bg-gradient-to-t from-base-300/90 to-transparent opacity-60 transition-opacity duration-300 group-hover:opacity-40 pointer-events-none" />
                </div>

                <div className="p-6 flex flex-col gap-4 flex-1">
                    <div className="flex gap-2 flex-wrap">
                        {Object.entries(blog.tags || {}).map(([key, value]) => (
                            <span key={key} className="badge badge-primary badge-sm border-none text-primary-content">
                                {key}: {value}
                            </span>
                        ))}
                    </div>

                    <div className="flex-1">
                        <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors">
                            {blog.title}
                        </h3>
                        <p className="text-base-content/70 line-clamp-3 text-sm">
                            {blog.description || "No description available."}
                        </p>
                    </div>

                    <div className="flex items-center justify-between text-xs text-base-content/50 mt-auto pt-4 border-t border-base-content/5">
                        <span>{formattedDate}</span>
                        <span className="group-hover:translate-x-1 transition-transform">
                            Read Article →
                        </span>
                    </div>
                </div>
            </Link>

            {showEdit && (
                <Link
                    to={`/edit/${blog.id}`}
                    className="absolute top-3 right-3 z-10 rounded-lg bg-base-100/90 px-2 py-1 text-xs font-medium text-base-content/70 shadow-sm hover:text-primary transition-colors"
                >
                    Edit
                </Link>
            )}
        </article>
    );
}
